import React, { useState, useEffect } from 'react';
import { Workplace, DangerClass } from '../../types';
import { workplaceApi } from '../../services/api';
import Swal from 'sweetalert2';
import './EditWorkplaceModal.css';

interface EditWorkplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  workplace: Workplace | null;
  onSaved: (updatedWorkplace: Workplace) => void;
}

export const EditWorkplaceModal: React.FC<EditWorkplaceModalProps> = ({
  isOpen,
  onClose,
  workplace,
  onSaved
}) => {
  const [formData, setFormData] = useState({
    title: '',
    dangerClass: 'low' as DangerClass,
    employeeCount: 1,
    sgkNo: '',
    taxNo: '',
    city: '',
    district: '',
    address: '',
    contactPerson: '',
    contactPhone: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (workplace && isOpen) {
      setFormData({
        title: workplace.title || '',
        dangerClass: workplace.dangerClass || 'low',
        employeeCount: workplace.employeeCount || 1,
        sgkNo: workplace.sgkNo || '',
        taxNo: workplace.taxNo || '',
        city: workplace.city || '',
        district: workplace.district || '',
        address: workplace.address || '',
        contactPerson: workplace.contactPerson || '',
        contactPhone: workplace.contactPhone || '',
        notes: workplace.notes || ''
      });
    }
  }, [workplace, isOpen]);

  if (!isOpen || !workplace) return null;

  const formatPhoneNumber = (value: string) => {
    let digits = value.replace(/\D/g, '');
    while (digits.startsWith('0')) {
      digits = digits.substring(1);
    }
    if (digits.length > 0 && !digits.startsWith('5')) {
      digits = '5' + digits;
    }
    digits = digits.slice(0, 10);

    let formatted = '';
    if (digits.length > 0) formatted += digits.substring(0, 3);
    if (digits.length > 3) formatted += ' ' + digits.substring(3, 6);
    if (digits.length > 6) formatted += ' ' + digits.substring(6, 8);
    if (digits.length > 8) formatted += ' ' + digits.substring(8, 10);
    return formatted;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'contactPhone') {
      setFormData(prev => ({ ...prev, contactPhone: formatPhoneNumber(value) }));
      return;
    }

    if (name === 'sgkNo') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 26);
      setFormData(prev => ({ ...prev, sgkNo: digitsOnly }));
      return;
    }

    if (name === 'employeeCount') {
      const val = value === '' ? 1 : Math.max(1, parseInt(value, 10) || 1);
      setFormData(prev => ({ ...prev, employeeCount: val }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik Bilgi',
        text: 'Lütfen firma / işyeri unvanını giriniz.',
        confirmButtonColor: '#0284c7'
      });
      return;
    }

    if (formData.employeeCount < 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Geçersiz Değer',
        text: 'Çalışan sayısı en az 1 olmalıdır.',
        confirmButtonColor: '#0284c7'
      });
      return;
    }

    try {
      setSaving(true);
      const res = await workplaceApi.update(workplace.id, {
        title: formData.title.trim(),
        dangerClass: formData.dangerClass,
        employeeCount: Number(formData.employeeCount),
        sgkNo: formData.sgkNo.trim(),
        taxNo: formData.taxNo.trim(),
        city: formData.city.trim(),
        district: formData.district.trim(),
        address: formData.address.trim(),
        contactPerson: formData.contactPerson.trim(),
        contactPhone: formData.contactPhone.trim(),
        notes: formData.notes.trim()
      });

      if (res.success && res.data) {
        Swal.fire({
          icon: 'success',
          title: 'Güncellendi',
          text: 'İşyeri bilgileri başarıyla güncellendi.',
          timer: 1600,
          showConfirmButton: false
        });
        onSaved(res.data);
        onClose();
      }
    } catch (err: any) {
      console.error('İşyeri güncellenirken hata:', err);
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.response?.data?.message || 'İşyeri güncellenirken bir hata oluştu.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="edit-wp-overlay" onClick={onClose}>
      <div className="edit-wp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-wp-header">
          <div className="edit-wp-title-group">
            <h3>✏️ İşyeri Bilgilerini Düzenle</h3>
            <p className="edit-wp-subtitle">{workplace.title}</p>
          </div>
          <button type="button" className="edit-wp-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-wp-form">
          <div className="edit-wp-grid">
            {/* Firma Unvanı */}
            <div className="edit-field full">
              <label>Firma / İşyeri Unvanı <span className="req">*</span></label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="Örn: ABC Metal Sanayi Ltd. Şti."
                className="edit-input font-bold"
              />
            </div>

            {/* Tehlike Sınıfı */}
            <div className="edit-field">
              <label>Tehlike Sınıfı</label>
              <select
                name="dangerClass"
                value={formData.dangerClass}
                onChange={handleChange}
                className="edit-select"
              >
                <option value="low">Az Tehlikeli (Büro / Hizmet)</option>
                <option value="dangerous">Tehlikeli (Üretim / İmalat)</option>
                <option value="veryDangerous">Çok Tehlikeli (İnşaat / Ağır Sanayi)</option>
              </select>
            </div>

            {/* Çalışan Sayısı */}
            <div className="edit-field">
              <label>Çalışan Sayısı <span className="req">*</span></label>
              <input
                type="number"
                name="employeeCount"
                min={1}
                required
                value={formData.employeeCount}
                onChange={handleChange}
                className="edit-input"
              />
            </div>

            {/* SGK / DETSİS No */}
            <div className="edit-field">
              <label>SGK Sicil No / DETSİS</label>
              <input
                type="text"
                name="sgkNo"
                value={formData.sgkNo}
                onChange={handleChange}
                placeholder="SGK veya 8 haneli DETSİS"
                className="edit-input font-mono"
              />
            </div>

            {/* Vergi No */}
            <div className="edit-field">
              <label>Vergi No / T.C.</label>
              <input
                type="text"
                name="taxNo"
                value={formData.taxNo}
                onChange={handleChange}
                placeholder="Vergi Kimlik Numarası"
                className="edit-input font-mono"
              />
            </div>

            {/* İl */}
            <div className="edit-field">
              <label>İl</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Örn: Ankara"
                className="edit-input"
              />
            </div>

            {/* İlçe */}
            <div className="edit-field">
              <label>İlçe</label>
              <input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleChange}
                placeholder="Örn: Yenimahalle"
                className="edit-input"
              />
            </div>

            {/* Yetkili Kişi */}
            <div className="edit-field">
              <label>Yetkili Kişi</label>
              <input
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                placeholder="Yetkili Adı Soyadı"
                className="edit-input"
              />
            </div>

            {/* İletişim Telefonu */}
            <div className="edit-field">
              <label>İletişim Telefonu</label>
              <input
                type="text"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleChange}
                placeholder="05XX XXX XX XX"
                maxLength={14}
                className="edit-input font-mono"
              />
            </div>

            {/* Açık Adres */}
            <div className="edit-field">
              <label>Açık Adres</label>
              <textarea
                name="address"
                rows={2}
                value={formData.address}
                onChange={handleChange}
                placeholder="İşyerinin tam açık adresi..."
                className="edit-textarea"
              />
            </div>

            {/* Özel İSG Notları */}
            <div className="edit-field">
              <label>Özel İSG Notları</label>
              <textarea
                name="notes"
                rows={2}
                value={formData.notes}
                onChange={handleChange}
                placeholder="İşyeri ve saha hakkında özel notlar..."
                className="edit-textarea"
              />
            </div>
          </div>

          <div className="edit-wp-actions">
            <button
              type="button"
              className="btn-edit-cancel"
              onClick={onClose}
              disabled={saving}
            >
              İptal
            </button>
            <button
              type="submit"
              className="btn-edit-save"
              disabled={saving}
            >
              {saving ? 'Kaydediliyor...' : '💾 Değişiklikleri Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditWorkplaceModal;
